import React, { useEffect, useRef } from 'react';
import { Globe, Zap, ShieldCheck, Settings } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WhyUsSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const benefits = [
    { icon: Globe, ...t.whyUs.benefit1 },
    { icon: Zap, ...t.whyUs.benefit2 },
    { icon: ShieldCheck, ...t.whyUs.benefit3 },
    { icon: Settings, ...t.whyUs.benefit4 },
  ];

  return (
    <section 
      id="why-us" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-black relative"
      data-testid="why-us-section"
    >
      <div className="absolute inset-0 dot-pattern opacity-20" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4">
            {t.whyUs.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white">
            {t.whyUs.title}
          </h2>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="animate-on-scroll"
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="card-premium p-8 md:p-10 min-h-[260px] group">
                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center border border-white/10 mb-8 group-hover:border-white/30 transition-colors duration-500">
                  <benefit.icon className="w-6 h-6 text-white/50 group-hover:text-white transition-colors duration-500" />
                </div>

                {/* Content */}
                <h3 className="font-sans text-sm tracking-[0.1em] uppercase font-medium text-white mb-4">
                  {benefit.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed font-light">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
